import {
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ScoreCardsFeedbacksModal } from "@/pages/recruit/candidates/widgets/ScoreCardsFeedbacksModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useCandidateFeedbacksOverview } from "@/queries/recruit/use-scorecards.query.ts";
import { ApiFormScoreMark, scoreMarkColors } from "@/types/forms/form.types.ts";
import { ApiCandidateFeedback, ApiScoreCardStatus } from "@/types/recruit/scorecard.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export function ScoreCardFeedbacksOverview({
  candidateId,
  applicantId,
  isCollapsed,
}: {
  candidateId: number;
  applicantId: number;
  isCollapsed: boolean;
}) {
  const { openModal } = useModals();
  const navigate = useNavigate();
  const { data: me } = useEmployeeMe();
  const { isFeatureEnabled } = usePermissions();
  const { renderEmployeeName } = useFormatter();
  const { data: feedbacksOverview } = useCandidateFeedbacksOverview({
    candidateId: candidateId,
    enabled: !!candidateId && !isCollapsed && isFeatureEnabled(ApiFeatureType.ats_scorecards),
  });
  const { t } = useTranslation();

  const feedbacksComputedInfo = useMemo(() => {
    const counters: Record<ApiFormScoreMark, { priority: number; count: number }> = {
      [ApiFormScoreMark.none]: { priority: 0, count: 0 },
      [ApiFormScoreMark.low]: { priority: 25, count: 0 },
      [ApiFormScoreMark.average]: { priority: 50, count: 0 },
      [ApiFormScoreMark.high]: { priority: 100, count: 0 },
    };
    let countMarks = 0;
    const countersSorted = [];
    const myFeedbacks: ApiCandidateFeedback[] =
      feedbacksOverview?.feedbacks.filter(
        (feedback) =>
          feedback.employee.id == me?.id && feedback.status == ApiScoreCardStatus.invited
      ) ?? [];

    feedbacksOverview?.score_marks.forEach((score_mark) => {
      counters[score_mark].count++;
      countMarks++;
    });

    for (const key in counters) {
      const state = key as ApiFormScoreMark;

      countersSorted.push({
        counter: counters[state],
        state,
      });
    }

    countersSorted.sort((a, b) => a.counter.priority - b.counter.priority);

    return {
      counters: countersSorted,
      countMarks,
      myFeedbacks,
    };
  }, [feedbacksOverview]);

  const viewAllFeedbacks = () => {
    openModal(ScoreCardsFeedbacksModal, { candidateId, applicantId });
  };

  if (
    feedbacksComputedInfo.myFeedbacks.length == 0 &&
    !(feedbacksOverview && feedbacksOverview.requested_count > 0)
  ) {
    return null;
  }
  return (
    <Stack gap={"md"} className={"py-4 border-b border-dark/10"}>
      {feedbacksComputedInfo.myFeedbacks.length > 0 && (
        <DropdownMenu
          trigger={
            <Button
              leftIcon={<FontAwesomeIcon icon="fa-light fa-message-captions" />}
              rightIcon={
                <NotificationCount count={feedbacksComputedInfo.myFeedbacks.length} size={"sm"} />
              }
            >
              {t("common:leave_feedback")}
            </Button>
          }
        >
          {feedbacksComputedInfo.myFeedbacks.map((feedback) => (
            <DropdownMenuItem
              key={`my-feedback-${feedback.id}`}
              onClick={() => navigate(`/feedbacks/${feedback.id}/candidates/${candidateId}`)}
              title={feedback.scorecard.title}
            />
          ))}
        </DropdownMenu>
      )}
      {feedbacksOverview && feedbacksOverview.requested_count > 0 && (
        <Stack className={"cursor-pointer"} gap={"sm"} onClick={viewAllFeedbacks}>
          <Text className={"font-semibold text-default text-base"}>
            {feedbacksOverview.avg_scale_score
              ? `${t("scorecards:avg_score")}: ${Number(feedbacksOverview.avg_scale_score.toFixed(2))}`
              : t("scorecards:has_feedback")}
            {feedbacksOverview.completed_count < feedbacksOverview.requested_count &&
              ` (${t("common:from_count_of", {
                count: feedbacksOverview.completed_count,
                count_all: feedbacksOverview.requested_count,
              })})`}
          </Text>
          <Stack className={"h-1 rounded-xs overflow-hidden bg-dark/5"} direction={"horizontal"}>
            {feedbacksComputedInfo.counters.map((item) => (
              <Stack
                key={`info-line-${item.counter.priority}`}
                className={twMerge("h-1", scoreMarkColors[item.state])}
                style={{
                  width: `${(item.counter.count * 100) / feedbacksComputedInfo.countMarks}%`,
                }}
              />
            ))}
          </Stack>
          <Text className={"text-secondary text-base"}>
            {feedbacksOverview.feedbacks
              .slice(0, 3)
              .map((feedback) => renderEmployeeName(feedback.employee))
              .join(", ")}
            {feedbacksOverview.feedbacks.length > 3 && " ..."}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}
